import * as Popover from '@radix-ui/react-popover';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  EstimatedVideoDuration,
  formatEstimatedDurationButtonLabel,
  type EstimatedDurationState,
} from './estimated-video-duration';
import { OUT_LANG_OPTIONS, OutputLangSelect } from './output-lang-select';
import { SettingIcon } from './assets/setting-icon';

/** 输出设置按钮（输出语言、视频时长） */
export const OutputSettingButton: React.FC<{
  outLang: string;
  setOutLang: (val: string) => void;
  estimatedDuration: EstimatedDurationState;
  setEstimatedDuration: (val: EstimatedDurationState) => void;
  disabled?: boolean;
}> = ({ outLang, setOutLang, estimatedDuration, setEstimatedDuration, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const langLabel = OUT_LANG_OPTIONS.find((o) => o.value === outLang)?.label ?? '';
  const durationLabel = formatEstimatedDurationButtonLabel(estimatedDuration);

  return (
    <Popover.Root modal={false} open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 h-9 pl-2 pr-3 rounded-[10px] bg-transparent',
            'text-sm font-medium text-[rgba(16,24,40,1)] select-none outline-none',
            'hover:bg-[rgba(242,244,247,1)] transition-colors',
            'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[rgba(16,24,40,0.12)]',
            disabled && 'cursor-not-allowed opacity-50',
            open && 'bg-[rgba(242,244,247,1)]',
          )}
        >
          <SettingIcon className="flex-shrink-0" />
          <span className="text-[rgba(16,24,40,1)] text-xs truncate max-w-[100px]">{langLabel}</span>
          <span className="h-3 w-px flex-shrink-0 bg-[rgba(208,213,221,1)]" aria-hidden />
          <span className="text-[rgba(16,24,40,1)] text-xs">{durationLabel}s</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <>
          {open && (
            <div
              className="fixed inset-0 z-[55] bg-transparent"
              onClick={() => setOpen(false)}
              aria-hidden
            />
          )}
          <Popover.Content
            side="top"
            align="center"
            sideOffset={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className={cn(
              'z-[60] w-[460px] min-w-0 rounded-[16px] border border-[rgba(234,236,240,1)] bg-white p-4',
              '[box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            )}
          >
            <div className="flex w-full min-w-0 flex-col gap-5">
              {/* 输出语言选择 */}
              <div className="flex w-full min-w-0 flex-col gap-2">
                <p className="text-xs text-[rgba(152,162,179,1)] font-medium px-0.5">
                  {t('ai_creation.select_output_language')}
                </p>
                <OutputLangSelect
                  outLang={outLang}
                  disabled={disabled}
                  setOutLang={setOutLang}
                  popoverSide="right"
                  popoverAlign="start"
                  sideOffset={20}
                  popoverAlignOffset={-130}
                  triggerClassName="w-full min-w-0"
                  anchorToTriggerEnd
                  avoidCollisions={false}
                  selectClassName="min-w-[256px] min-h-[171px] rounded-[16px] border border-[rgba(234,236,240,1)] bg-white [box-shadow:0px_4px_6px_-2px_rgba(16,_24,_40,_0.03),_0px_12px_16px_-4px_rgba(16,_24,_40,_0.08)] z-[70]"
                  trigger={
                    <button
                      type="button"
                      className={cn(
                        'w-full flex items-center gap-2 rounded-[8px] border border-[rgba(234,236,240,1)]',
                        'bg-transparent py-3 px-3 text-left text-sm font-medium text-[rgba(16,24,40,1)]',
                        'hover:bg-[rgba(16,24,40,0.03)] transition-colors outline-none',
                        'focus-visible:ring-2 focus-visible:ring-[rgba(16,24,40,0.12)]',
                        disabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <span className="flex-1 truncate">{langLabel}</span>
                      <ChevronRight className="size-4 flex-shrink-0 text-[rgba(152,162,179,1)]" />
                    </button>
                  }
                />
              </div>
              {/* 视频时长设置 */}
              <EstimatedVideoDuration value={estimatedDuration} onChange={setEstimatedDuration} />
            </div>
          </Popover.Content>
        </>
      </Popover.Portal>
    </Popover.Root>
  );
};
